import React from "react";
import GridListTable, {
  GridListTableHeaderItemProps,
} from "@/shared/components/GridListTable";
import useI18nHelper from "@/features/ui/hooks/use-i18n-helper";
import { Box, Flex, Text, Tooltip } from "@chakra-ui/react";
import CustomIcon from "../../../../../../../public/icons-v1_1";
import HelpTooltip from "@/shared/components/help-tooltip";
import { UnoFee } from "@/features/my-order/features/settlement-management/types";
import { formatDateToYMDEGeneral, formatDateToYMGeneral } from "@/shared/utils";
import useSettlementManagementQuery from "@/features/my-order/features/settlement-management/hooks/useSettlementManagementQuery";
import NoResultCustom from "@/shared/components/no-result-simple";
import useRouterHelper from "@/features/ui/hooks/use-router-helper";

interface Props {
  unoFeeList: UnoFee[];
}

const templateColumns = "200px 120px 100px";

function SettlementManagementBillingHistoryList({ unoFeeList }: Props) {
  const { tTitle, tSentence, isKor } = useI18nHelper();
  const { query, setQuery } = useSettlementManagementQuery();
  const { calcNavigateTo } = useRouterHelper();

  const currentDate = query.date ? new Date(query.date) : new Date();
  const month = currentDate.toLocaleString("en-US", { month: "long" });
  const formattedCurrentMonth = formatDateToYMGeneral(currentDate);

  const headers: GridListTableHeaderItemProps[] = [
    { label: tTitle("settlement:order_history"), align: "left" },
    {
      label: "final_customs_date",
      component: (
        <Flex gap={"4px"} alignItems={"center"} justifyContent={"center"}>
          <Text textStyle={"body-sm-bold"} color={"#444444"}>
            {tTitle("settlement:final_customs_date")}
          </Text>
          <HelpTooltip
            label={tSentence("settlement:fee_tooltip.final_customs_date")}
          />
        </Flex>
      ),
    },
    {
      label: tTitle("settlement:fee_amount"),
      align: "right",
    },
  ];

  return (
    <Flex flexDirection={"column"} gap={"16px"}>
      <Text textStyle={"body-lg-bold"}>
        {tTitle(`date.${month.toLowerCase()}_short`)}{" "}
        {tSentence("settlement:billing_history")}
      </Text>
      <GridListTable templateColumns={templateColumns}>
        <GridListTable.Header headers={headers} />
        <Flex
          flexDirection={"column"}
          gap={"12px"}
          height={"385px"}
          overflowY={"auto"}
          padding={"1px"}
        >
          {unoFeeList?.length === 0 && (
            <NoResultCustom
              size={"48px"}
              text={tSentence("settlement:no_fee_result_warning")}
            />
          )}
          {unoFeeList?.map((row, index) => {
            const showCarriedOver =
              row.isDelayed && row.settlementMonth !== formattedCurrentMonth;
            // 오더 시트 세트 없이 올린 프로포마도 수수료 행으로 내려온다. 그때는 프로포마 이름을 보여주고 주문 링크는 뺀다.
            const firstOrderSheetSet = row.orderSheetSets[0];

            const rowList = [
              <Flex
                key={"order-history"}
                gap={"4px"}
                alignItems={"center"}
                width={"100%"}
                cursor={"pointer"}
              >
                <Text
                  noOfLines={1}
                  textStyle={"body-sm-bold"}
                  wordBreak={"break-all"}
                  color={"#444444"}
                >
                  {firstOrderSheetSet
                    ? `#${firstOrderSheetSet.lineSheetSet.id} ${firstOrderSheetSet.lineSheetSet.name}`
                    : row.proformaInvoice.name}
                </Text>
                {firstOrderSheetSet && (
                  <Tooltip
                    label={tTitle(
                      "settlement:fee_tooltip.open_order_with_new_tab"
                    )}
                  >
                    <Box
                      data-row-action
                      color={"#8F8F8C"}
                      _hover={{
                        color: "#1272EF",
                      }}
                      _active={{
                        color: "#00417D",
                      }}
                      onClick={(e) => {
                        e.stopPropagation();
                        window.open(
                          calcNavigateTo(
                            `/order-sheet-sets/order-history?order_sheet_set_id=${firstOrderSheetSet.id}`
                          ),
                          "_blank"
                        );
                      }}
                      onMouseEnter={(e) => e.stopPropagation()}
                    >
                      <CustomIcon src={"arrow-square"} width={16} height={16} />
                    </Box>
                  </Tooltip>
                )}
              </Flex>,
              <Text
                key={"final-customs-date"}
                textStyle={"body-sm-regular"}
                color={"#444444"}
              >
                {formatDateToYMDEGeneral(
                  row.finalCustomsClearanceDate ?? "",
                  true,
                  "-"
                )}
              </Text>,
              <Flex
                key={"fee-amount"}
                flexDirection={"column"}
                gap={"4px"}
                width={"100%"}
                alignItems={"end"}
              >
                <Text
                  textAlign={"end"}
                  width={"100%"}
                  textStyle={"body-sm-bold"}
                  color={showCarriedOver ? "#C0C0C0" : "#444444"}
                  textDecoration={showCarriedOver ? "line-through" : ""}
                >
                  {`${isKor ? "" : "₩"}${row.unoFeeAmount.toLocaleString()}${
                    isKor ? "원" : ""
                  }`}
                </Text>
                {showCarriedOver && (
                  <Flex gap={"2px"} alignItems={"center"}>
                    <CustomIcon
                      src={"warning-circle-solid"}
                      color={"#E5B400"}
                      width={16}
                      height={16}
                    />
                    <Text textStyle={"caption-regular"} color={"#B27700"}>
                      {tSentence("settlement:billing_carried_over")}
                    </Text>
                  </Flex>
                )}
              </Flex>,
            ];

            return (
              <GridListTable.Row
                key={index}
                items={rowList}
                onRowClick={() =>
                  setQuery((prev) => ({
                    ...prev,
                    unoFeeId: row.id,
                  }))
                }
                hasAction={false}
                hasScroll={true}
                cursor={"pointer"}
                sx={{
                  "&:hover:not(:has([data-row-action]:hover))": {
                    backgroundColor: "#F7F9FA",
                    boxShadow: "0 0 0 0.5px #1272EF",
                  },
                  "&:active:not(:has([data-row-action]:active))": {
                    backgroundColor: "#EFEFED",
                    boxShadow: "0 0 0 0.5px #00417D",
                  },
                }}
                itemProps={{
                  justifyContent: "center",
                }}
              />
            );
          })}
        </Flex>
      </GridListTable>
    </Flex>
  );
}

export default SettlementManagementBillingHistoryList;
